import React, { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { ToastContext, ToastDataType, ToastOptions } from '@shared/lib/toast'

const maxToasts = 5
const defaultDuration = 5000

const ToastProvider: FC<PropsWithChildren> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastDataType[]>([])

  const toast = useCallback((options: ToastOptions) => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const newToast: ToastDataType = {
      id,
      ...options,
      duration: options.duration ?? defaultDuration,
    }

    setToasts((prev) => {
      const updated = [...prev, newToast]

      return updated.slice(-maxToasts)
    })

    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, newToast.duration)
    }

    return id
  }, [])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const dismissAll = useCallback(() => {
    setToasts([])
  }, [])

  const memoizedToasts = useMemo(() => toasts, [toasts])

  const value = useMemo(
    () => ({ toasts: memoizedToasts, toast, dismiss, dismissAll }),
    [memoizedToasts, toast, dismiss, dismissAll]
  )

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export default ToastProvider
